import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import DispatchOrderArticlesList from "./DispatchOrderArticlesList";
import {
  buildDispatchMovementForAdditionalItems,
  buildDispatchOrderWizardStep,
  buildOrderDispatchBatchPayload,
  calculateDispatchOrderStepTotals,
} from "helpers/orderDispatch";
import { useToast } from "hooks/useToast";
import {
  AdditionalItem,
  Article,
  DispatchOrderWizardStep,
  OrderDraftArticleRow,
  Order,
  OrderBatchDispatchPayload,
  Store,
  User,
  Client,
  AssociatedClient,
} from "types";
import MovementAdditionalItemsStepModal from "components/Operations/MovementAdditionalItemsStepModal";

type Props = {
  open: boolean;
  loading?: boolean;
  orders: Order[];
  clients: Client[];
  associatedClients: AssociatedClient[];
  articles: Article[];
  additionalItems: AdditionalItem[];
  stores: Store[];
  users: User[];
  onClose: () => void;
  onConfirm: (payload: OrderBatchDispatchPayload) => Promise<boolean>;
};

export default function DispatchOrdersWizardModal({
  open,
  loading = false,
  orders,
  clients,
  associatedClients,
  articles,
  additionalItems,
  stores,
  users,
  onClose,
  onConfirm,
}: Props) {
  const { showSnackBar } = useToast();
  const [activeIndex, setActiveIndex] = useState(0);
  const [steps, setSteps] = useState<DispatchOrderWizardStep[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveIndex(0);
    setSteps(orders.map(buildDispatchOrderWizardStep));
  }, [open, orders]);

  const currentOrder = orders[activeIndex] ?? null;
  const currentStep = steps[activeIndex] ?? null;

  const currentTotals = useMemo(() => {
    if (!currentStep) {
      return {
        totalPallets: 0,
        totalUnits: 0,
        totalWeight: 0,
      };
    }

    return calculateDispatchOrderStepTotals({
      step: currentStep,
      articles,
    });
  }, [articles, currentStep]);

  const createdByLabel = useMemo(() => {
    if (!currentOrder) {
      return "-";
    }

    const createdByUser = users.find(
      (user) => user.id === Number(currentOrder.created_by),
    );

    return createdByUser
      ? `${createdByUser.first_name} ${createdByUser.last_name}`.trim()
      : "-";
  }, [currentOrder, users]);

  const preparedByLabel = useMemo(() => {
    if (!currentOrder) {
      return "-";
    }

    const preparedByUser = users.find(
      (user) => user.id === Number(currentOrder.updated_by),
    );

    return preparedByUser
      ? `${preparedByUser.first_name} ${preparedByUser.last_name}`.trim()
      : "-";
  }, [currentOrder, users]);

  const storeLabel = useMemo(() => {
    if (!currentOrder) {
      return "-";
    }

    if (currentOrder.store_id == null) {
      return "Todos";
    }

    return (
      stores.find((store) => store.id === Number(currentOrder.store_id))
        ?.description ?? "-"
    );
  }, [currentOrder, stores]);

  const childClients = useMemo(
    () =>
      clients.filter(
        (client) => client.parent_client_id === currentOrder?.client_id,
      ),
    [clients, currentOrder?.client_id],
  );

  const clientAssociatedClients = useMemo(
    () =>
      associatedClients.filter(
        (associatedClient) =>
          associatedClient.client_id === currentOrder?.client_id &&
          (associatedClient.is_active ||
            associatedClient.id === currentStep?.associated_client_id),
      ),
    [associatedClients, currentOrder?.client_id, currentStep?.associated_client_id],
  );

  const selectedAssociatedClient = useMemo(
    () =>
      clientAssociatedClients.find(
        (associatedClient) =>
          associatedClient.id === currentStep?.associated_client_id,
      ) ?? null,
    [clientAssociatedClients, currentStep?.associated_client_id],
  );

  const dispatchMovementForAdditionalItems = useMemo(
    () =>
      currentOrder && currentStep
        ? buildDispatchMovementForAdditionalItems({
            order: currentOrder,
            step: currentStep,
          })
        : null,
    [currentOrder, currentStep],
  );

  const updateCurrentStep = (
    updater: (step: DispatchOrderWizardStep) => DispatchOrderWizardStep,
  ) => {
    setSteps((prev) =>
      prev.map((step, index) =>
        index === activeIndex ? updater(step) : step,
      ),
    );
  };

  const validateSteps = () => {
    for (let index = 0; index < steps.length; index += 1) {
      const step = steps[index];

      if (!step.date) {
        showSnackBar(
          `El pedido #${step.order_id} debe tener una fecha de egreso.`,
          "warning",
        );
        setActiveIndex(index);
        return false;
      }

      if (!step.client_document.trim()) {
        showSnackBar(
          `El pedido #${step.order_id} debe tener documento del cliente.`,
          "warning",
        );
        setActiveIndex(index);
        return false;
      }

      if (step.articles.length === 0) {
        showSnackBar(
          `El pedido #${step.order_id} debe conservar al menos un artículo.`,
          "warning",
        );
        setActiveIndex(index);
        return false;
      }

      const invalidArticle = step.articles.find(
        (article: OrderDraftArticleRow) =>
          !Number.isInteger(Number(article.amount)) || Number(article.amount) <= 0,
      );

      if (invalidArticle) {
        showSnackBar(
          `El pedido #${step.order_id} tiene artículos con cantidad inválida.`,
          "warning",
        );
        setActiveIndex(index);
        return false;
      }

      const order = orders.find(
        (currentOrder) => Number(currentOrder.id) === Number(step.order_id),
      );

      const validLineClientIds = clients
        .filter((client) => client.parent_client_id === order?.client_id)
        .map((client) => Number(client.id));

      const invalidAdditionalItem = step.additional_items.find((item) => {
        const lineClientId = Number(item.line_client_id);

        return (
          !Number.isInteger(lineClientId) ||
          lineClientId <= 0 ||
          !validLineClientIds.includes(lineClientId) ||
          !Number.isInteger(Number(item.amount)) ||
          Number(item.amount) <= 0 ||
          item.price == null ||
          !Number.isFinite(Number(item.price))
        );
      });

      if (invalidAdditionalItem) {
        showSnackBar(
          `El pedido #${step.order_id} tiene adicionales incompletos o sin línea válida.`,
          "warning",
        );
        setActiveIndex(index);
        return false;
      }
    }

    return true;
  };

  const handleConfirm = async () => {
    if (!validateSteps()) {
      return;
    }

    const success = await onConfirm(buildOrderDispatchBatchPayload(steps));

    if (success) {
      onClose();
    }
  };

  if (!currentOrder || !currentStep) {
    return null;
  }

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96%", xl: "82rem" },
          maxHeight: "92vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="h5">
                  Despachar pedidos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pedido {activeIndex + 1} de {steps.length} - #{currentOrder.id}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ py: 0 }}>
                El pedido se ajustará a las cantidades finales despachadas antes de generar el egreso.
              </Alert>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Usuario" value={createdByLabel} fullWidth disabled />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Preparó" value={preparedByLabel} fullWidth disabled />
          </Grid>

          <Grid item xs={12} md={4}>
            <DatePicker
              label="Fecha"
              value={currentStep.date}
              format="dd/MM/yyyy"
              onChange={(value) =>
                updateCurrentStep((step) => ({
                  ...step,
                  date: value,
                }))
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Cliente"
              value={`${currentOrder.client?.code ?? ""} - ${currentOrder.client?.razon_social ?? ""}`.trim()}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={clientAssociatedClients}
              value={selectedAssociatedClient}
              getOptionLabel={(option) => option.razon_social}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) =>
                updateCurrentStep((step) => ({
                  ...step,
                  associated_client_id: value?.id ?? null,
                }))
              }
              disabled={loading}
              renderInput={(params) => (
                <TextField {...params} label="Cliente Asociado" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Documento del Cliente"
              value={currentStep.client_document}
              onChange={(event) =>
                updateCurrentStep((step) => ({
                  ...step,
                  client_document: event.target.value,
                }))
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Almacén" value={storeLabel} fullWidth disabled />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Pallets"
              type="number"
              value={currentStep.pallets ?? currentTotals.totalPallets}
              onChange={(event) =>
                updateCurrentStep((step) => ({
                  ...step,
                  pallets:
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                }))
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Peso"
              type="number"
              value={currentStep.weight ?? currentTotals.totalWeight}
              onChange={(event) =>
                updateCurrentStep((step) => ({
                  ...step,
                  weight:
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                }))
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Número de Remito"
              value={currentStep.numero_remito}
              onChange={(event) =>
                updateCurrentStep((step) => ({
                  ...step,
                  numero_remito: event.target.value,
                }))
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Observaciones"
              value={currentStep.observation}
              onChange={(event) =>
                updateCurrentStep((step) => ({
                  ...step,
                  observation: event.target.value,
                }))
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Artículos a despachar
            </Typography>
            <DispatchOrderArticlesList
              rows={currentStep.articles}
              articles={articles}
              client={currentOrder.client ?? null}
              disabled={loading}
              onEditRow={(row) =>
                updateCurrentStep((step) => ({
                  ...step,
                  articles: step.articles.map((currentRow) =>
                    currentRow.localId === row.localId ? row : currentRow,
                  ),
                }))
              }
            />

            <Grid
              container
              spacing={1}
              sx={{ mt: 1 }}
              justifyContent="flex-start"
            >
              <Grid item xs={12} md={3}>
                <TextField
                  label="Cantidad Total"
                  value={String(currentTotals.totalPallets)}
                  fullWidth
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Total Unidad"
                  value={String(currentTotals.totalUnits)}
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Items adicionales
            </Typography>

            {dispatchMovementForAdditionalItems && (
              <MovementAdditionalItemsStepModal
                embedded
                open
                title=""
                movement={dispatchMovementForAdditionalItems}
                additionalItems={additionalItems}
                childClients={childClients}
                associatedClients={clientAssociatedClients}
                initialItems={currentStep.additional_items}
                onClose={() => undefined}
                onConfirm={() => undefined}
                onChange={({ movement, items }) =>
                  updateCurrentStep((step) => ({
                    ...step,
                    date: movement.date ? new Date(movement.date) : step.date,
                    client_document: movement.client_document ?? step.client_document,
                    numero_remito: movement.numero_remito ?? step.numero_remito,
                    observation: movement.observation ?? step.observation,
                    additional_items: items,
                  }))
                }
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Button variant="outlined" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>

              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  disabled={loading || activeIndex === 0}
                  onClick={() => setActiveIndex((prev) => prev - 1)}
                >
                  Atrás
                </Button>

                {activeIndex < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={() => setActiveIndex((prev) => prev + 1)}
                  >
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={() => void handleConfirm()}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Confirmar"
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
